/**
 * Où va un média de la toolbox — la cible, séparée de ce qui s'y pose. Chaque éditeur la
 * déduit de son état (la page sélectionnée, un calque qui prend une image, une séquence) et
 * la PUBLIE à chaque changement (`toolbox:placement`) : la toolbox l'affiche en tête de ses
 * pages médias, et la fige au dépôt d'un upload — le média uploadé arrive là où il a été
 * demandé, même si la sélection a bougé entre-temps.
 *
 * Les cibles désignent par id (page, calque, séquence) : une cible disparue entre le dépôt et
 * l'arrivée ne se rabat sur rien, le média reste simplement dans la grille.
 */
import { t } from "./i18n";

export type Placement =
  /** Le fond d'une page de l'éditeur images. */
  | { action: "background"; page: string }
  /** L'image d'un calque qui en prend une (image, forme, ImageFrame). */
  | { action: "replace"; layer: string }
  /** Un calque image de plus — sur cette page (éditeur images), ou au visuel (`page: null`). */
  | { action: "insert"; page: string | null }
  /** Le fond d'une séquence du compositeur vidéo. */
  | { action: "segment-background"; segment: string }
  /** Une séquence de plus, en fin de timeline. */
  | { action: "append-segment" };

/** La cible de chaque famille de média : un éditeur ne pose pas une vidéo comme une image. */
export interface Placements {
  image: Placement | null;
  video: Placement | null;
}

export type MediaKind = keyof Placements;

/** La famille d'un média de la bibliothèque — un PDF se pose comme une image. */
export const mediaKind = (item: { media_type?: string }): MediaKind =>
  item.media_type === "video" ? "video" : "image";

/** Une cible qui ne prend qu'UN média : d'un lot uploadé, seul le premier s'y pose. */
export const isExclusive = (placement: Placement): boolean =>
  ["background", "replace", "segment-background"].includes(placement.action);

/** Le titre des pages médias de la toolbox : ce que fera un clic, ou un upload. Le fond
 *  d'une page et celui d'une séquence se disent pareil. */
export function placementLabel(placement: Placement | null): string {
  if (!placement) return "";

  const key = placement.action === "segment-background" ? "background" : placement.action.replaceAll("-", "_");
  return t(`toolbox.placement.${key}`);
}

export const PLACEMENT_EVENT = "toolbox:placement";

// La dernière cible publiée : les éditeurs republient à chaque mutation, seul un changement
// part ; la toolbox la lit en arrivant, si elle se connecte après la publication.
let published: Placements | null = null;
let publishedKey = "";

/** L'éditeur annonce sa cible courante (la toolbox écoute sur window) — rien si elle n'a pas
 *  changé. */
export function publishPlacements(placements: Placements): void {
  const key = JSON.stringify(placements);
  if (key === publishedKey) return;

  published = placements;
  publishedKey = key;
  window.dispatchEvent(new CustomEvent(PLACEMENT_EVENT, { detail: placements }));
}

export const publishedPlacements = (): Placements | null => published;
